import { clsx } from '@alexis/helpers/clsx';
import SortButton from '@pages/FlightSearchResult/components/NewSortBar/SortButton';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchSort from '@pages/FlightSearchResult/hooks/useFlightSearchSort';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Skeleton from '@components/Skeleton';

import { useViewportSize } from '@hooks/useViewportSize';

import { getTranslationsState } from '@redux/selectors';

import { FlightSortType } from '@wegoTypes/flights/flightSortType';
import { SortOrder } from '@wegoTypes/sortOrder';

import styles from './NewSortBar.module.scss';

interface NewSortBarProps {
  onSortTypeChanged(sortType: FlightSortType, sortOrder: SortOrder): void;
}

interface FlightSortButton {
  id: FlightSortType;
  tooltip?: string;
  translationKey: string;
  disabled?: boolean;
  defaultSortOrder?: SortOrder;
  offSetLeftId?: string;
}

const NewSortBar = ({ onSortTypeChanged }: NewSortBarProps) => {
  const translations = useSelector(getTranslationsState);
  const { width } = useViewportSize();
  const isAbove1280 = width >= 1280;
  const { sortType, sortOrder } = useFlightSearchSort();
  const { isLoading, finalTripViewModelsToShowBasedOnSortType } = useFlightSearchResults();
  const { isPLSDepartLegSelection, isPerLegSearch, isPLSChangeLegLoading } = useFlightPLS();

  const isFirstLoading = isLoading && finalTripViewModelsToShowBasedOnSortType.length === 0;

  const flightSortButtons = useMemo<FlightSortButton[]>(() => {
    return [
      {
        id: 'score',
        translationKey: 'recommended',
        defaultSortOrder: 'desc',
        disabled: sortType === 'score',
        tooltip: translations.recommended_sort_type_description as string,
      },
      {
        id:
          isPerLegSearch && !isPLSDepartLegSelection
            ? 'leg2_departure_time'
            : 'leg1_departure_time',
        translationKey: 'departure_time',
        offSetLeftId: 'flightCardLegDepartureTime',
      },
      {
        id: 'duration',
        translationKey: 'duration',
        offSetLeftId: 'flightCardDurationAndStop',
        disabled: sortType === 'duration',
      },
      { id: 'price', translationKey: 'price', disabled: sortType === 'price' },
    ];
  }, [
    isPLSDepartLegSelection,
    isPerLegSearch,
    sortType,
    translations.recommended_sort_type_description,
  ]);

  const handleSortTypeChange = (clickedSort: FlightSortType, defaultSortOrder?: SortOrder) => {
    if (clickedSort === sortType) {
      onSortTypeChanged(clickedSort, sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      onSortTypeChanged(clickedSort, defaultSortOrder || 'asc');
    }
  };

  return (
    <div className={clsx(styles.container, isFirstLoading && styles.loading)}>
      {isFirstLoading || isPLSChangeLegLoading ? (
        <Skeleton width='100%' height={44} borderRadius={8} />
      ) : (
        <>
          {isAbove1280 && <div className={styles.sortBy}>{translations.sort_by}</div>}
          {flightSortButtons.map((b, index) => (
            <SortButton
              key={b.id}
              tooltip={b.tooltip}
              disabled={b.disabled}
              isActive={b.id === sortType}
              label={translations[b.translationKey] as string}
              offSetLeftId={b.offSetLeftId}
              defaultSortOrder={b.defaultSortOrder}
              isLast={index === flightSortButtons.length - 1}
              onClick={() => handleSortTypeChange(b.id, b.defaultSortOrder)}
            />
          ))}
        </>
      )}
    </div>
  );
};

export default NewSortBar;
